import { Component, OnInit, OnDestroy, NgZone, ViewChild } from '@angular/core';

import { Subscription } from 'rxjs';
import { ModalDirective } from 'ngx-bootstrap/modal';

import {
    Document,
    DocumentService,
    Attribute,
    AttributeMaps,
    DocumentPartMap,
    AttributeService,
    DataArray,
    AuthorizationService
} from './../../../shared/index';

@Component({
    selector: 'pmdocument-list',
    templateUrl: './pmdocument-list.html',
    standalone: false
})

export class PMDocumentListComponent implements OnInit, OnDestroy {

    @ViewChild('confirmPrint') confirmPrintModal: ModalDirective;
    @ViewChild('confirmNoPrint') confirmNoPrintModal: ModalDirective;
    @ViewChild('documentFilter') documentFilter: ModalDirective;

    private subscriptions: Subscription[] = [];

    public attributes: Attribute[] = [];

    public documents: DataArray<Document> = new DataArray<Document>(1);

    public selectedModels: DocumentPartMap[] = [];
    public selectedPoweredBys: DocumentPartMap[] = [];
    public selectedSuites: DocumentPartMap[] = [];
    public selectedLanguages: DocumentPartMap[] = [];
    public selectedDocuments: DataArray<Document> = new DataArray<Document>(1);

    public filteredDocuments: Document[] = [];

    public processing = true;
    public ascending = true;
    public total = 0;
    public filterApplied = false;
    public fileName = '';
    public ableToPrint = true;
    public notAbleToPrint = true;
    public reloadDocuments = false;
    public previousSelectedDocId = 0;
    public currentSelectedDocId = 0;
    public documentIndexMap = [] as any;
    public currentUser = '';

    constructor(
        private _ngZone: NgZone,
        private _attributeService: AttributeService,
        private _documentService: DocumentService,
        private _authorizationService: AuthorizationService,
    ) {
        this.subscriptions.push(this._documentService.documentsChange.subscribe((documents: Document[]) => {
            this._ngZone.run(() => {

                this.total = this._documentService.totalDocuments;

                this.documents.init();
                this.filteredDocuments = [];
                this.currentUser = this._authorizationService.userName;
                //this.currentPage = this._documentService.currentPage;

                let index = 1;
                for (const document of documents) {
                    this.documents.update(document, true);
                    // this dictionary array will store the order of document(Index) and corresponding unique ID of the Document
                    this.documentIndexMap.push({'Index': index, 'Id': document.Id});
                    index++;
                }

                // if reloading the documents when printing preference have changed, reapply the filter
                if (this.reloadDocuments) {

                    this.reloadDocuments = false;

                    // reset the filter if applied
                    if (this.fileName != '' || this.selectedModels.length > 0 || this.selectedPoweredBys.length > 0 || this.selectedSuites.length > 0 || this.selectedLanguages.length > 0 || this.ableToPrint == false || this.notAbleToPrint == false) {
                        this._documentService.applyFilter(this.fileName, this.selectedModels, this.selectedPoweredBys, this.selectedSuites, this.selectedLanguages, this.ableToPrint, this.notAbleToPrint);
                    }
                }

                this.filteredDocuments = this.documents.Data;
                this.processing = false;
            });
        }));

        this.subscriptions.push(this._documentService.documentSelectedChange.subscribe((document: Document) => {
            if (document.Selected) {
                this.selectedDocuments.update(document, true);
            } else {
                this.selectedDocuments.delete(document);
            }
        }));

        this.subscriptions.push(this._documentService.clearSelected.subscribe((result: boolean) => {
            for (const document of this.documents.Data.filter((doc) => {
                return doc.Selected;
            })) {
                document.Selected = false;
            }
        }));

        this.subscriptions.push(this._documentService.printChange.subscribe((result: boolean) => {
            this.processing = true;
            this._documentService.getAllDocuments();
        }));


        this.subscriptions.push(this._attributeService.attributesChange.subscribe((attributes: Attribute[]) => {
            this._ngZone.run(() => {
                this.attributes = attributes;
                for (const attribute of this.attributes) {
                    attribute.Maps.init();
                    this._attributeService.getMaps(attribute.Id);
                }
            });
        }));

        this.subscriptions.push(this._attributeService.attributeMapChange.subscribe((attributeMaps: AttributeMaps) => {
            this._ngZone.run(() => {
                const attribute: Attribute = this.attributes.filter((value) => value.Id == attributeMaps.AttributeId)[0];
                attribute.Maps.init();
                for (const map of attributeMaps.Maps) {
                    attribute.Maps.update(map, true);
                }
            });
        }));
    }

    ngOnInit(): void {
        this._attributeService.getAttributes();
        this.selectedDocuments.init();
        this.documents.init();

        this._documentService.getAllDocuments();
    }

    ngOnDestroy(): void {
        for (const subscription of this.subscriptions) {
            subscription.unsubscribe();
        }
    }

    public selectDocument(document: Document, docIndex: number, event: MouseEvent): void {
        // set prev and curr selected docIndex for multi select between shift logic
        if (!document.Selected) { // set prev and current only if the document is not selected already.
            if (this.previousSelectedDocId == 0) {
                this.previousSelectedDocId = docIndex; // set prev from docIndex the very first time. No need to set curr for the very first time
            } else {
                if (this.currentSelectedDocId != 0) { // don't set prev to curr when curr is not set yet (first time)
                    this.previousSelectedDocId = this.currentSelectedDocId;
                }
                this.currentSelectedDocId = docIndex;
            }
        }

        if (!event.shiftKey) {
            document.Selected = !document.Selected;
            this._documentService.selectDocument(document);
        } else if (this.currentSelectedDocId != 0 && this.previousSelectedDocId != 0 && this.currentSelectedDocId > this.previousSelectedDocId) {
            this.getShiftSelectionDocuments(this.previousSelectedDocId, this.currentSelectedDocId).forEach(shiftDocument => {
                // select only the documents that are not selected already
                if (this.selectedDocuments.Data.filter(sd => sd.Id == shiftDocument.Id).length == 0) {
                    shiftDocument.Selected = true;
                    this._documentService.selectDocument(shiftDocument);
                }

            });
        }
    }

    public clearSelectedDocuments(): void {
        this.selectedDocuments.init();
        this._documentService.clearAll();
        this.previousSelectedDocId = 0;
        this.currentSelectedDocId = 0;
    }

    public promptPrintConfirm(): void {
        this.confirmPrintModal.show();
    }
    public promptNoPrintConfirm(): void {
        this.confirmNoPrintModal.show();
    }
    public cancelPrint(): void {
        this.confirmPrintModal.hide();
    }
    public cancelNoPrint(): void {
        this.confirmNoPrintModal.hide();
    }

    public allowPrint(): void {
        const inDocIds: number[] = [];
        const idx = 0;
        for (let i = 0; i < this.selectedDocuments.Data.length; i++) {
            inDocIds.push(this.selectedDocuments.Data[i].Id)
        }

        this.processing = true;
        this.reloadDocuments = true;
        this._documentService.allowDocumentsToPrint(inDocIds, this.currentUser);
        this.clearSelectedDocuments();
        this.confirmPrintModal.hide();
    }

    public restrictPrint(): void {
        const inDocIds: number[] = [];
        const idx = 0;
        for (let i = 0; i < this.selectedDocuments.Data.length; i++) {
            inDocIds.push(this.selectedDocuments.Data[i].Id)
        }

        this.processing = true;
        this.reloadDocuments = true;
        this._documentService.restrictDocumentsFromPrint(inDocIds, this.currentUser);
        this.clearSelectedDocuments();
        this.confirmNoPrintModal.hide();
    }
    public showFilters(): void {
        this.documentFilter.show();
    }

    public applyFilter(): void {
        if (this.fileName != '' || this.selectedModels.length > 0 || this.selectedPoweredBys.length > 0 || this.selectedSuites.length > 0 || this.selectedLanguages.length > 0 || this.ableToPrint == false || this.notAbleToPrint == false) {
            this.filterApplied = true;
        } else {
            this.filterApplied = false;
        }
        this.processing = true;
        this._documentService.applyFilter(this.fileName, this.selectedModels, this.selectedPoweredBys, this.selectedSuites, this.selectedLanguages, this.ableToPrint, this.notAbleToPrint);
        this.documentFilter.hide();
    }

    public clearFilter(): void {
        this.fileName = '';
        this.selectedModels = [];
        this.selectedPoweredBys = [];
        this.selectedSuites = [];
        this.selectedLanguages = [];
        this.filterApplied = false;
        this.ableToPrint = true;
        this.notAbleToPrint = true;
        this.processing = true;
        this._documentService.applyFilter(this.fileName, this.selectedModels, this.selectedPoweredBys, this.selectedSuites, this.selectedLanguages, this.ableToPrint, this.notAbleToPrint);
    }

    private getShiftSelectionDocuments(previous: number, current: number): Document[] {
        const docIndices: number[] = [];

        for (let i = previous + 1; i <= current; i++) { // previous + 1 to skip the previous which is selected already
            docIndices.push(i);
        }

        const docIndexMaps = this.documentIndexMap.filter((dim: { Index: number; }) => docIndices.indexOf(dim.Index) >= 0);
        const docIDs = [...Array.from(new Set(docIndexMaps.map((dim: { Id: any; }) => dim.Id)))];

        return this.documents.Data.filter(doc => docIDs.indexOf(doc.Id) >= 0);

    }
}
